import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Trash2, MapPin, FileText, AlertCircle, Calendar, Plus, Clock } from "lucide-react";
import PickupDatePicker from "./PickupDatePicker";
import { fetchLimaDistrictCatalog, isAfternoonOnlyDistrict } from "../../../utils/locationCatalog";

const brand = {
  primary: "#B1336E",
  accentGold: "#E6B43C",
  accentGreen: "#67A43C",
};

// Helper function to get current Peru time
function getCurrentPeruTime() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Lima' }));
}

// Check if selected date is today (Peru time)
function isToday(selectedDateStr) {
  if (!selectedDateStr) return false;
  
  // Parse the selected date components
  let selectedYear, selectedMonth, selectedDay;
  
  if (selectedDateStr.includes('/')) {
    // Handle DD/MM/YYYY format
    const parts = selectedDateStr.split('/');
    selectedDay = parseInt(parts[0], 10);
    selectedMonth = parseInt(parts[1], 10);
    selectedYear = parseInt(parts[2], 10);
  } else if (selectedDateStr.includes('-')) {
    // Handle YYYY-MM-DD format (standard ISO format)
    const parts = selectedDateStr.split('-');
    selectedYear = parseInt(parts[0], 10);
    selectedMonth = parseInt(parts[1], 10);
    selectedDay = parseInt(parts[2], 10);
  } else {
    console.error('Unknown date format:', selectedDateStr);
    return false;
  }
  
  // Get current Peru time components
  const peruTime = getCurrentPeruTime();
  const todayYear = peruTime.getFullYear();
  const todayMonth = peruTime.getMonth() + 1; // JavaScript months are 0-indexed
  const todayDay = peruTime.getDate();
  
  // Compare date components directly (avoids UTC conversion issues)
  const result = (
    selectedYear === todayYear && 
    selectedMonth === todayMonth && 
    selectedDay === todayDay
  );
  
  // Debug logging (remove in production)
  console.log('📅 isToday check:', {
    input: selectedDateStr,
    parsed: `${selectedYear}-${selectedMonth}-${selectedDay}`,
    today: `${todayYear}-${todayMonth}-${todayDay}`,
    result
  });
  
  return result;
}

// Check if current time is past morning cutoff (7:00 AM Peru time)
function isPastMorningCutoff(selectedDateStr) {
  // If no date selected or date is in the future, morning slot is available
  if (!selectedDateStr || !isToday(selectedDateStr)) {
    return false;
  }
  
  // Only apply time cutoff if the selected date is TODAY
  const peruTime = getCurrentPeruTime();
  const currentHour = peruTime.getHours();
  return currentHour >= 7; // After 7am, morning slot is no longer available FOR TODAY
}

// Check if current time is past afternoon cutoff (varies by district)
function isPastAfternoonCutoff(selectedDateStr, isFarDistrict) {
  // If no date selected or date is in the future, afternoon slot is available
  if (!selectedDateStr || !isToday(selectedDateStr)) {
    return false;
  }
  
  const peruTime = getCurrentPeruTime();
  const currentHour = peruTime.getHours();
  
  if (isFarDistrict) {
    return currentHour >= 11; // Far districts: cutoff at 11am
  } else {
    return currentHour >= 13; // Near districts: cutoff at 1pm (13:00)
  }
}

export default function Recojo({ setActiveComponent }) {
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTimeSlot, setPickupTimeSlot] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [districtCatalog, setDistrictCatalog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [restrictionReason, setRestrictionReason] = useState("");

  useEffect(() => {
    const fetchAddresses = async () => {
      const userId = localStorage.getItem("user_id");
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/user_addresses/${userId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const nonArchived = (res.data.addresses || []).filter((a) => !a.archived);
        setAddresses(nonArchived);
      } catch (e) {
        console.error("Error fetching addresses:", e);
      }
    };
    fetchAddresses();
  }, []);

  useEffect(() => {
    fetchLimaDistrictCatalog()
      .then(setDistrictCatalog)
      .catch(() => setDistrictCatalog([]));
  }, []);

  // Define time slots with their details
  const TIME_SLOTS = [
    {
      value: "morning",
      label: "Mañana (6am - 9am)",
      pickupTime: "6:00 AM - 9:00 AM",
      deliveryTime: "10:00 AM - 8:00 PM",
      description: "Recojo temprano, entrega el mismo día"
    },
    {
      value: "afternoon",
      label: "Tarde (10am - 1pm)",
      pickupTime: "10:00 AM - 1:00 PM",
      deliveryTime: "2:00 PM - 8:00 PM",
      description: "Recojo al mediodía, entrega en la tarde"
    }
  ];

  // Update available time slots when district OR pickup date changes
  useEffect(() => {
    if (selectedDistrict && pickupDate) {
      const isFarDistrict = isAfternoonOnlyDistrict(selectedDistrict, districtCatalog);

      // Check time cutoffs
      const morningPastCutoff = isPastMorningCutoff(pickupDate);
      const afternoonPastCutoff = isPastAfternoonCutoff(pickupDate, isFarDistrict);

      let availableSlots = [];
      let reason = "";

      // Determine available slots
      if (isFarDistrict) {
        // Far districts: Never show morning slot
        if (!afternoonPastCutoff) {
          availableSlots = [TIME_SLOTS[1]]; // Only afternoon
          reason = "district_far";
        } else {
          // No slots available for today
          availableSlots = [];
          reason = "afternoon_cutoff_far";
        }
      } else {
        // Near districts: Both slots potentially available
        if (!morningPastCutoff) {
          availableSlots.push(TIME_SLOTS[0]); // Morning available
        }
        if (!afternoonPastCutoff) {
          availableSlots.push(TIME_SLOTS[1]); // Afternoon available
        }
        
        if (availableSlots.length === 0) {
          reason = "all_cutoffs_passed";
        } else if (availableSlots.length === 1 && availableSlots[0].value === "afternoon") {
          reason = "morning_cutoff_only";
        }
      }

      setAvailableTimeSlots(availableSlots);
      setRestrictionReason(reason);

      // Auto-select if only one option
      if (availableSlots.length === 1) {
        setPickupTimeSlot(availableSlots[0].value);
      } else if (availableSlots.length === 0) {
        setPickupTimeSlot("");
      } else {
        // Multiple options available, reset if needed
        if (pickupTimeSlot === "" || !availableSlots.find(s => s.value === pickupTimeSlot)) {
          setPickupTimeSlot("");
        }
      }
    } else {
      setAvailableTimeSlots([]);
      setPickupTimeSlot("");
      setRestrictionReason("");
    }
  }, [selectedDistrict, pickupDate, districtCatalog, pickupTimeSlot]);

  const handleAddressSelect = (addressId) => {
    const sel = addresses.find((a) => String(a.id) === String(addressId));
    const district = sel?.district || "";
    
    // Set the district for time slot logic
    setSelectedDistrict(district);

    setSelectedAddress(addressId);
    setErrorMessage("");
  };

  const handleDeleteSelected = async () => {
    if (!selectedAddress) return;
    const ok = window.confirm("¿Estás seguro de eliminar esta dirección?");
    if (!ok) return;

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/archive_address/${selectedAddress}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAddresses((prev) => prev.filter((a) => String(a.id) !== String(selectedAddress)));
      setSelectedAddress("");
      setSelectedDistrict("");
      setPickupTimeSlot("");
      alert("La dirección ha sido eliminada correctamente.");
    } catch (e) {
      console.error("Error al archivar la dirección:", e.response?.data || e.message);
      alert("Hubo un error al intentar eliminar la dirección.");
    }
  };

  const handleConfirmClick = () => {
    if (!selectedAddress || !pickupDate) {
      setErrorMessage("Selecciona una dirección y una fecha de recojo.");
      return;
    }
    if (!pickupTimeSlot) {
      setErrorMessage("Selecciona un horario de recojo.");
      return;
    }
    if (availableTimeSlots.length === 0) {
      setErrorMessage("No hay horarios disponibles para hoy. Por favor selecciona una fecha futura.");
      return;
    }
    setErrorMessage("");
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    setIsModalOpen(false);
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token available.");
      return;
    }

    try {
      setBusy(true);
      const payload = {
        user_id: localStorage.getItem("user_id"),
        pickup_date: pickupDate || null,
        pickup_time_slot: pickupTimeSlot,
        notes: additionalNotes,
        address_id: selectedAddress,
      };

      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/recojo`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Response from server:", res.data);
      
      // Show success modal after successful submission
      setIsSuccessModalOpen(true);
    } catch (e) {
      console.error("Error creating recojo:", e.response?.data || e.message);
      if (e.response?.data?.message) {
        setErrorMessage(e.response.data.message);
      } else {
        setErrorMessage("Hubo un error al solicitar el recojo. Por favor intenta de nuevo.");
      }
    } finally {
      setBusy(false);
    }
  };

  // Get selected time slot details
  const selectedSlotDetails = TIME_SLOTS.find(slot => slot.value === pickupTimeSlot);

  // Generate restriction message
  const getRestrictionMessage = () => {
    const peruTime = getCurrentPeruTime();
    const currentTimeStr = peruTime.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: true });

    switch (restrictionReason) {
      case "district_far":
        return (
          <>
            Debido a la distancia de <span className="font-semibold">{selectedDistrict}</span>, 
            solo está disponible el horario de recojo de la tarde (10am-1pm) para garantizar la entrega el mismo día.
          </>
        );
      
      case "afternoon_cutoff_far":
        return (
          <>
            Ya no hay horarios disponibles para hoy en <span className="font-semibold">{selectedDistrict}</span>. 
            Son las {currentTimeStr} y el límite para solicitar recojo es 11:00 AM para tu distrito. 
            Por favor selecciona una fecha futura.
          </>
        );
      
      case "morning_cutoff_only":
        return (
          <>
            El horario de recojo de la mañana (6am-9am) ya no está disponible <strong>para hoy</strong> porque son más de las 7:00 AM. 
            Solo puedes solicitar recojo para el horario de la tarde (10am-1pm).
          </>
        );
      
      case "all_cutoffs_passed":
        return (
          <>
            Ya no hay horarios disponibles para hoy. Son las {currentTimeStr} y el límite para solicitar recojo 
            es 1:00 PM. Por favor selecciona una fecha futura.
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="h-screen w-full bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col overflow-hidden">
      {/* Header - Fixed */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="mx-auto max-w-4xl px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveComponent('Analytics')}
              className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Volver</span>
            </button>
            <h1 className="text-lg sm:text-xl font-bold" style={{ color: brand.primary }}>
              Solicitar Recojo
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
          {/* Info Card */}
          <div className="mb-6 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 p-4">
            <p className="text-sm text-slate-700">
              📦 Ahora ofrecemos <span className="font-semibold" style={{ color: brand.primary }}>entregas el mismo día</span> según tu ubicación y horario de recojo
            </p>
          </div>

          {/* Form Card */}
          <div className="rounded-2xl bg-white shadow-lg border border-slate-200">
            <div className="p-6 space-y-6">
              {/* Date Selection using PickupDatePicker */}
              <PickupDatePicker 
                selectedDate={pickupDate}
                onDateChange={setPickupDate}
                accentColor={brand.accentGold}
              />

              {/* Address Selection */}
              <div>
                <label className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-800">
                  <MapPin className="h-4 w-4" style={{ color: brand.primary }} />
                  Dirección de recojo
                </label>
                <div className="flex gap-2">
                  <select
                    value={selectedAddress}
                    onChange={(e) => handleAddressSelect(e.target.value)}
                    className="flex-1 rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-opacity-50 transition-all"
                    style={{ focusBorderColor: brand.primary }}
                  >
                    <option value="">Selecciona una dirección</option>
                    {addresses.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.address}
                      </option>
                    ))}
                  </select>
                  
                  <button
                    type="button"
                    onClick={() => setActiveComponent('MapSelection')}
                    className="flex items-center justify-center rounded-xl px-4 py-3 transition-all bg-green-50 text-green-600 hover:bg-green-100 border-2 border-green-200"
                    title="Agregar nueva dirección"
                  >
                    <Plus className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={handleDeleteSelected}
                    disabled={!selectedAddress}
                    className={`flex items-center justify-center rounded-xl px-4 py-3 transition-all ${
                      selectedAddress
                        ? "bg-red-50 text-red-600 hover:bg-red-100 border-2 border-red-200"
                        : "bg-slate-100 text-slate-400 border-2 border-slate-200 cursor-not-allowed"
                    }`}
                    title="Eliminar dirección"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-2 text-xs text-slate-500 flex items-start gap-1">
                  <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  Si no ves tu dirección, haz clic en el botón <span className="font-semibold text-green-600">+</span> para agregar una nueva.
                </p>
              </div>

              {/* Time Slot Selection - Only show when address AND date are selected */}
              {selectedAddress && pickupDate && availableTimeSlots.length > 0 && (
                <div>
                  <label className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-800">
                    <Clock className="h-4 w-4" style={{ color: brand.accentGold }} />
                    Horario de recojo
                  </label>
                  
                  <div className="space-y-3">
                    {availableTimeSlots.map((slot) => {
                      const isOnlyOption = availableTimeSlots.length === 1;
                      const isSelected = pickupTimeSlot === slot.value;
                      
                      return (
                        <button
                          key={slot.value}
                          type="button"
                          onClick={() => setPickupTimeSlot(slot.value)}
                          className={`w-full rounded-xl border-2 p-4 text-left transition-all ${
                            isSelected
                              ? 'border-amber-400 bg-amber-50'
                              : 'border-slate-200 bg-white hover:border-amber-200 hover:bg-amber-50/50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-bold text-slate-800">
                                  {slot.label}
                                </span>
                                {isOnlyOption && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                                    Único disponible
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-600 mb-2">
                                {slot.description}
                              </p>
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div>
                                  <span className="text-slate-500">Recojo:</span>
                                  <span className="ml-1 font-medium text-slate-700">
                                    {slot.pickupTime}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Entrega:</span>
                                  <span className="ml-1 font-medium text-slate-700">
                                    {slot.deliveryTime}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className={`flex-shrink-0 ml-3 ${
                              isSelected ? 'text-amber-500' : 'text-slate-300'
                            }`}>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                isSelected 
                                  ? 'border-amber-500 bg-amber-500' 
                                  : 'border-slate-300'
                              }`}>
                                {isSelected && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Delivery time customization notice */}
                  <div className="mt-3 flex items-start gap-2 rounded-xl bg-green-50 border border-green-200 px-4 py-3">
                    <AlertCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-green-700">
                      💡 El destinatario final podrá elegir su hora preferida de entrega a través del enlace de seguimiento que le enviaremos.
                    </p>
                  </div>

                  {/* Restriction notice */}
                  {restrictionReason && (
                    <div className="mt-3 flex items-start gap-2 rounded-xl bg-blue-50 border border-blue-200 px-4 py-3">
                      <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-blue-700">
                        {getRestrictionMessage()}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* No slots available message */}
              {selectedAddress && pickupDate && availableTimeSlots.length === 0 && (
                <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                  <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-700">
                    <p className="font-semibold mb-1">No hay horarios disponibles para hoy</p>
                    <p className="text-xs">{getRestrictionMessage()}</p>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-800">
                  <FileText className="h-4 w-4" style={{ color: brand.accentGreen }} />
                  Notas adicionales
                  <span className="text-xs font-normal text-slate-500">(opcional)</span>
                </label>
                <textarea
                  rows={3}
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  placeholder="Ej: Portón gris al lado de la farmacia, preguntar por Juan..."
                  className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-opacity-50 transition-all resize-none"
                  style={{ focusBorderColor: brand.accentGreen }}
                  maxLength={500}
                />
                <p className="mt-1 text-xs text-slate-400 text-right">
                  {additionalNotes.length}/500
                </p>
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                  <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Fixed */}
      <div className="bg-white border-t border-slate-200 shadow-lg">
        <div className="mx-auto max-w-4xl px-4 py-4 sm:px-6">
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={() => setActiveComponent('Analytics')}
              className="rounded-xl border-2 border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmClick}
              disabled={busy || (selectedAddress && pickupDate && availableTimeSlots.length === 0)}
              className="rounded-xl px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              style={{ backgroundColor: brand.primary }}
            >
              {busy ? "Procesando..." : "Confirmar Recojo"}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full" style={{ backgroundColor: `${brand.primary}20` }}>
                  <Calendar className="h-6 w-6" style={{ color: brand.primary }} />
                </div>
                <h3 className="text-lg font-bold" style={{ color: brand.primary }}>
                  Confirmar Solicitud
                </h3>
              </div>
              
              <div className="mb-6 space-y-3">
                <p className="text-sm text-slate-600">
                  ¿Está seguro de que la información ingresada para el recojo es correcta?
                </p>
                {selectedSlotDetails && (
                  <div className="rounded-lg bg-slate-50 p-3 space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Fecha:</span>
                      <span className="font-medium text-slate-700">
                        {pickupDate ? new Date(pickupDate + 'T00:00:00').toLocaleDateString('es-PE') : ''}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Horario de recojo:</span>
                      <span className="font-medium text-slate-700">
                        {selectedSlotDetails.pickupTime}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Ventana de entrega:</span>
                      <span className="font-medium text-slate-700">
                        {selectedSlotDetails.deliveryTime}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 rounded-xl border-2 border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Revisar
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all"
                  style={{ backgroundColor: brand.primary }}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {isSuccessModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <Calendar className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-green-600">
                  ¡Recojo Solicitado!
                </h3>
              </div>
              
              <p className="text-sm text-slate-600 mb-6">
                Tu solicitud de recojo ha sido registrada exitosamente. ¿Qué deseas hacer ahora?
              </p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => setActiveComponent('Entrega')}
                  className="w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all"
                  style={{ backgroundColor: brand.primary }}
                >
                  Agregar Entrega
                </button>
                <button
                  onClick={() => setActiveComponent('Analytics')}
                  className="w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Mis Entregas
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
